import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { useUnityBuildStore } from '../../stores/store';
import { useSetSelectedAssemblyFromAssemblyPicker } from '../../hooks/useSetSelectedAssembly';
// Local or old custom types:
import { TakeoffAssemblies, TakeoffAssembly } from '../../types/AssemblyItemType';
// Possibly: import your new proto definitions if you prefer
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  useTheme,
  alpha,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import CreateAssembly from '../assembliesAndItems/assemblies/CreateAssembly';
import { useHandleGlobalStateState } from '../../hooks/useHandleGlobalState';
import { useDeleteTakeoffAssembly } from '../../hooks/useDeleteTakeoffAssembly';
import { filterAssembliesByBreakout, hasQuotedItems } from './palletteHelpers';
import {
  useFetchEstimateBreakouts,
  useFetchChangeOrderBreakouts,
} from '../../hooks/useFetchBreakout';
// Removed unused import - useFetchCountedAssemblies
import { Calculations as calc } from '../../utils/calculations';
import { TakeoffRow } from './TakeoffRow';
import { shallow } from 'zustand/shallow';
import { useQueryClient } from 'react-query';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';
import { useSubmitTakeoffAssembly } from '../../hooks/useSubmitTakeoffAssembly';
import { usePreloadFoundryItems } from '../../hooks/usePreloadFoundryItems';

/** Example: compute item qty from the new single formula & breakout fields. */

// Re-add the missing interface
type RenderTakeoffListProps = {
  searchTerm: string;
};

function RenderTakeoffList({ searchTerm }: RenderTakeoffListProps) {
  const theme = useTheme();

  // Move hooks inside component
  const setSelectedAssembly = useSetSelectedAssemblyFromAssemblyPicker();
  const handleSetViewingState = useHandleGlobalStateState();
  const { deleteAssembly } = useDeleteTakeoffAssembly();
  const { queryKey } = useSubmitTakeoffAssembly();

  // Component state
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  // Remove unused forceRefresh state that was causing infinite loops

  // Add refs for scroll management
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [selectedRowElement, setSelectedRowElement] = useState<HTMLTableRowElement | null>(null);

  const { selectedObject, currentPage, selectedFilter, breakoutFilterObject } =
    useStoreWithEqualityFn(
      useUnityBuildStore,
      state => ({
        selectedObject: state.selectedObject,
        currentPage: state.currentPage,
        selectedFilter: state.selectedFilter,
        breakoutFilterObject: state.breakoutFilterObject,
      }),
      shallow
    );

  const { data: countedAssembliesData } = useFetchCountedAssemblies();

  // Preload BuildFoundry items when assemblies are loaded
  usePreloadFoundryItems();

  // Separate assembly store state with selectors
  const {
    assemblyOption,
    currentTakeoffAssembly,
    isViewingAssemblyForm,
    setIsViewingTakeoffEditor,
    setCurrentTakeoffAssembly,
    setIsViewingAssemblyForm,
    setAssemblyOption,
    setIsCreatingAssembly,
  } = useStoreWithEqualityFn(
    useAssemblyStore,
    state => ({
      assemblyOption: state.assemblyOption,
      currentTakeoffAssembly: state.currentTakeoffAssembly,
      isViewingAssemblyForm: state.isViewingAssemblyForm,
      setCurrentTakeoffAssembly: state.setCurrentTakeoffAssembly,
      setIsViewingAssemblyForm: state.setIsViewingAssemblyForm,
      setIsViewingTakeoffEditor: state.setIsViewingTakeoffEditor,
      setAssemblyOption: state.setAssemblyOption,
      setIsCreatingAssembly: state.setIsCreatingAssembly,
    }),
    shallow
  );

  const { setIsViewingColorEditor } = useStoreWithEqualityFn(
    useUnityBuildStore,
    state => ({
      setIsViewingColorEditor: state.setIsViewingColorEditor,
    }),
    shallow
  );

  // Get data from queries - use direct hooks for better cache invalidation detection
  const { selectedEstimate, selectedChangeOrder } = useStoreWithEqualityFn(
    useUnityBuildStore,
    state => ({
      selectedEstimate: state.selectedEstimate,
      selectedChangeOrder: state.selectedChangeOrder,
    }),
    shallow
  );

  const estimateBreakouts = useFetchEstimateBreakouts(selectedEstimate?.estimateid ?? '');
  const changeOrderBreakouts = useFetchChangeOrderBreakouts(
    selectedChangeOrder?.changeorderid ?? ''
  );

  // Select the appropriate breakout query based on current context
  const breakouts = selectedEstimate ? estimateBreakouts : changeOrderBreakouts;

  // Get query client to force refetch countedAssemblies when data changes
  const queryClient = useQueryClient();

  // Track countedAssemblies dataUpdatedAt to detect when data changes
  const countedAssembliesDataUpdatedAt = React.useMemo(() => {
    const queryData = queryClient.getQueryState(queryKey);
    return queryData?.dataUpdatedAt || 0;
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstimate, selectedChangeOrder, queryClient]); // Remove countedAssembliesData to prevent circular dependency

  // Memoize breakout data to prevent recalculations and include typical breakouts
  // Use breakouts data directly with dataUpdatedAt to detect changes
  const currentBreakouts = useMemo(() => {
    const breakoutsList = breakouts?.data?.breakoutsList ?? [];

    // Create combined list of standard breakouts and typical breakouts
    const result: any[] = [...breakoutsList];

    // Add typical breakouts as additional options for filtering
    breakoutsList.forEach(standardBreakout => {
      standardBreakout.typicalbreakoutsList?.forEach(typical => {
        result.push({
          ...standardBreakout,
          id: typical.id, // Use typical breakout ID
          breakoutname: `${standardBreakout.breakoutname} - ${typical.breakoutname}`,
          multiplier: typical.multiplier,
          isTypical: true,
          standardBreakoutId: standardBreakout.id,
          typicalBreakoutId: typical.id,
          _isTypical: true, // Add flag for filtering logic
        });
      });
    });

    return result;
  }, [breakouts?.data, breakouts?.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps -- dataUpdatedAt ensures refresh when breakouts change

  // Get filtered assemblies directly from countedAssembliesData
  // Use the pre-filtered data from useFetchCountedAssemblies for better consistency
  const filteredAssemblies = useMemo(() => {
    if (!countedAssembliesData || !selectedObject || !currentPage) return [];

    // Use the already filtered assemblies from the hook (filtered by objectid and pagenumber)
    const filteredByObjectAndPage = countedAssembliesData?.filteredAssemblies || [];

    if (filteredByObjectAndPage.length === 0) return [];

    // Apply additional breakout filtering if needed
    let finalFilteredAssemblies: TakeoffAssemblies[];

    if (breakoutFilterObject) {
      finalFilteredAssemblies = filterAssembliesByBreakout(
        filteredByObjectAndPage,
        selectedObject,
        currentPage,
        breakoutFilterObject
      );
    } else {
      finalFilteredAssemblies = filteredByObjectAndPage;
    }

    // Then apply breakout (assembly-level), type and search filters to individual assemblies
    return finalFilteredAssemblies.flatMap((asm: TakeoffAssemblies) =>
      asm.assembliesList.filter(assembly => {
        if (breakoutFilterObject) {
          const hasMatchingBreakoutItems = assembly.itemsList?.some(item => {
            if (!item?.breakout) return false;

            // Direct match on breakout ID
            if (item.breakout.breakoutid === breakoutFilterObject.id) {
              return true;
            }

            // If the selected breakout is a typical breakout, also match items
            // that have the standard breakout ID that this typical breakout references
            if (
              (breakoutFilterObject as any)._isTypical &&
              (breakoutFilterObject as any).standardBreakoutId
            ) {
              return item.breakout.breakoutid === (breakoutFilterObject as any).standardBreakoutId;
            }

            // If the selected breakout is a standard breakout, also match items
            // that have typical breakouts referencing this standard breakout
            if (!(breakoutFilterObject as any)._isTypical && item.breakout.typicalbreakoutid) {
              // This item uses a typical breakout, check if its standard breakout matches
              return item.breakout.breakoutid === breakoutFilterObject.id;
            }

            return false;
          });
          if (!hasMatchingBreakoutItems) {
            return false;
          }
        }
        // If a breakout filter is active, include all measurement types
        const typeMatches = breakoutFilterObject
          ? true
          : selectedFilter === 'all' ||
            assembly.assemblymeasurementtype.toLowerCase() === selectedFilter.toLowerCase();
        const searchTermNormalized = searchTerm.trim().toLowerCase();
        const searchMatches =
          searchTermNormalized === '' ||
          assembly.assemblyname.trim().toLowerCase().includes(searchTermNormalized);
        return typeMatches && searchMatches;
      })
    );
  }, [
    countedAssembliesData,
    selectedObject,
    currentPage,
    breakoutFilterObject,
    selectedFilter,
    searchTerm,
  ]);

  // Auto-scroll to selected assembly when it changes
  useEffect(() => {
    const assemblyId = currentTakeoffAssembly?.assemblyid;

    if (assemblyId && selectedRowElement && tableContainerRef.current) {
      // Use a small delay to ensure the DOM has updated
      const timeoutId = setTimeout(() => {
        if (selectedRowElement && tableContainerRef.current) {
          const container = tableContainerRef.current;
          const row = selectedRowElement;

          // Calculate if the row is visible in the container
          const containerRect = container.getBoundingClientRect();
          const rowRect = row.getBoundingClientRect();

          const isRowVisible =
            rowRect.top >= containerRect.top && rowRect.bottom <= containerRect.bottom;

          // Only scroll if the row is not visible
          if (!isRowVisible) {
            // Calculate the position to scroll to within the container
            const containerHeight = container.clientHeight;
            const rowOffsetTop = row.offsetTop;
            const rowHeight = row.offsetHeight;

            // Calculate the target scroll position to center the row
            // Add some padding so the row isn't exactly at the edge
            const padding = 50;
            let targetScrollTop;

            if (rowRect.top < containerRect.top) {
              // Row is above the visible area - scroll up
              targetScrollTop = rowOffsetTop - padding;
            } else if (rowRect.bottom > containerRect.bottom) {
              // Row is below the visible area - scroll down
              targetScrollTop = rowOffsetTop - containerHeight + rowHeight + padding;
            } else {
              // Row is partially visible - center it
              targetScrollTop = rowOffsetTop - containerHeight / 2 + rowHeight / 2;
            }

            // Ensure the scroll position is within bounds
            const maxScrollTop = container.scrollHeight - containerHeight;
            const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

            // Use smooth scrolling within the container only
            container.scrollTo({
              top: finalScrollTop,
              behavior: 'smooth',
            });
          }
        }
      }, 200); // Increased delay for better reliability

      return () => clearTimeout(timeoutId);
    }
  }, [currentTakeoffAssembly?.assemblyid, selectedRowElement]);

  // Clear selected row element when currentTakeoffAssembly is null/undefined
  useEffect(() => {
    const assemblyId = currentTakeoffAssembly?.assemblyid;
    if (!assemblyId) {
      setSelectedRowElement(null);
    }
  }, [currentTakeoffAssembly?.assemblyid]);

  // Use useCallback for event handlers with proper dependencies
  const handleSetAssembly = useCallback(
    (assembly: TakeoffAssembly) => {
      if (currentTakeoffAssembly?.assemblyid !== assembly.assemblyid) {
        // Set flag to indicate an active run is in progress
        // This ensures Point Editor shows "Complete Run" immediately
        useUnityBuildStore.getState().setHasActiveRun(true);
        setSelectedAssembly({
          takeoffAssembly: assembly,
        });
      }
    },
    [currentTakeoffAssembly, setSelectedAssembly]
  );

  const handleRenameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRenameValue(e.target.value);
  }, []);

  const handleRenameSubmit = useCallback(() => {
    setIsRenaming(false);
  }, []);

  const handleEditAssembly = useCallback(
    (assembly: TakeoffAssembly) => {
      handleSetViewingState({
        resetOption: 'resetAssembly',
        setStateFunctions: [
          {
            setStateFunction: setIsViewingAssemblyForm,
            prop: true,
          },
          {
            setStateFunction: setIsCreatingAssembly,
            prop: false,
          },
          {
            setStateFunction: setAssemblyOption,
            prop: 'takeoff',
          },
        ],
      });
      setSelectedAssembly(assembly as any);
      setCurrentTakeoffAssembly(assembly as TakeoffAssembly);
    },
    [
      handleSetViewingState,
      setIsViewingAssemblyForm,
      setIsCreatingAssembly,
      setAssemblyOption,
      setSelectedAssembly,
      setCurrentTakeoffAssembly,
    ]
  );

  const handleEditStyles = useCallback(
    (assembly: TakeoffAssembly) => {
      console.log('open the Color & Style Editor', assembly);
      setIsViewingColorEditor(true);
      setIsViewingTakeoffEditor(false);
    },
    [setIsViewingColorEditor, setIsViewingTakeoffEditor]
  );

  const handleSetShowDeleteWarning = useCallback(
    (assembly: TakeoffAssembly, show: boolean) => {
      setShowDeleteWarning(show);
      setCurrentTakeoffAssembly(assembly as TakeoffAssembly);
    },
    [setCurrentTakeoffAssembly]
  );

  const handleDeleteAssembly = useCallback(
    async (assembly: TakeoffAssembly) => {
      await deleteAssembly(assembly);
      setShowDeleteWarning(false);
    },
    [deleteAssembly]
  );

  const toggleExpandRow = useCallback((assemblyId: string) => {
    setExpandedRows(prev => {
      if (prev.includes(assemblyId)) {
        return prev.filter(id => id !== assemblyId);
      } else {
        return [...prev, assemblyId];
      }
    });
  }, []);

  // Memoize the row ref callback to prevent infinite loops
  const setRowRef = useCallback(
    (element: HTMLTableRowElement | null, isSelected: boolean) => {
      if (isSelected && element) {
        setSelectedRowElement(element);
      } else if (!isSelected && selectedRowElement && selectedRowElement === element) {
        // Clear the ref if this row is no longer selected
        setSelectedRowElement(null);
      }
    },
    [selectedRowElement]
  );

  // Added a memo for TableContainer to prevent unnecessary re-renders
  const optimizedTableContainer = useMemo(
    () => (
      <Box
        ref={tableContainerRef}
        sx={{
          height: '100%',
          minHeight: 0,
          overflow: 'auto',
          position: 'relative',
          backgroundColor: 'transparent',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          // Ensure container stays within its bounds and doesn't affect page layout
          containIntrinsicSize: 'none',
          isolation: 'isolate',
          zIndex: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '0px', // Hide horizontal scrollbar
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.05)
                : alpha(theme.palette.common.black, 0.05),
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '4px',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.2)
                : alpha(theme.palette.common.black, 0.2),
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.3)
                  : alpha(theme.palette.common.black, 0.3),
            },
          },
        }}
      >
        <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: '70%',
                  fontWeight: 600,
                  backgroundColor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                  py: 1,
                }}
              >
                Description
              </TableCell>
              <TableCell
                sx={{
                  width: '25%',
                  fontWeight: 600,
                  backgroundColor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                  py: 1,
                  textAlign: 'right',
                }}
              >
                Measurements
              </TableCell>
              <TableCell
                sx={{
                  width: '5%',
                  fontWeight: 600,
                  backgroundColor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                  py: 1,
                  textAlign: 'center',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAssemblies.map((assembly, index) => {
              const isExpanded = expandedRows.includes(assembly.assemblyid);
              const isSelected = currentTakeoffAssembly?.assemblyid === assembly.assemblyid;

              return (
                <TakeoffRow
                  key={assembly.assemblyid}
                  assembly={assembly}
                  index={index}
                  isSelected={isSelected}
                  isExpanded={isExpanded}
                  currentTakeoffAssembly={currentTakeoffAssembly}
                  currentBreakouts={currentBreakouts}
                  handleSetAssembly={handleSetAssembly}
                  toggleExpandRow={toggleExpandRow}
                  handleEditAssembly={handleEditAssembly}
                  handleEditStyles={handleEditStyles}
                  handleSetShowDeleteWarning={handleSetShowDeleteWarning}
                  hasMultiplierOverOne={calc.hasMultiplierOverOne}
                  hasQuotedItems={hasQuotedItems}
                  isRenaming={isRenaming}
                  renameValue={renameValue}
                  handleRenameChange={handleRenameChange}
                  handleRenameSubmit={handleRenameSubmit}
                  theme={theme}
                  selectedFilter={selectedFilter}
                  onRowRef={setRowRef}
                />
              );
            })}
          </TableBody>
        </Table>
      </Box>
    ),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filteredAssemblies,
      expandedRows,
      currentTakeoffAssembly,
      currentBreakouts,
      handleSetAssembly,
      toggleExpandRow,
      handleEditAssembly,
      handleSetShowDeleteWarning,
      handleEditStyles,
      theme,
      isRenaming,
      renameValue,
      handleRenameChange,
      handleRenameSubmit,
      selectedFilter,
      setRowRef,
      countedAssembliesDataUpdatedAt, // Include to re-render when assemblies update
    ]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Table Container */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          width: '100%',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Remove forceUpdateKey to preserve scroll position */}
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {optimizedTableContainer}
        </div>
      </Box>

      {showDeleteWarning && (
        <Dialog
          open={true}
          PaperProps={{
            sx: {
              p: 1,
            },
          }}
        >
          <DialogContent>
            <DialogContentText>Are you sure you want to delete this assembly?</DialogContentText>
            <DialogActions>
              <Button onClick={() => setShowDeleteWarning(false)} variant="outlined" size="small">
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteAssembly(currentTakeoffAssembly as TakeoffAssembly)}
                variant="contained"
                color="error"
                size="small"
              >
                Delete
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog for Creating or Editing an Assembly */}
      <Dialog open={isViewingAssemblyForm && assemblyOption === 'takeoff'} fullScreen={true}>
        <DialogContent>
          <CreateAssembly
            assemblyOption="takeoff"
            currentAssembly={currentTakeoffAssembly as TakeoffAssembly}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default React.memo(RenderTakeoffList);
